import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Sparkles, Save, Loader2, Megaphone, X } from 'lucide-react';
import { PROMO_COLOR_MAP } from '../PromoBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_OPTIONS: { key: string; label: string }[] = [
  { key: 'pink', label: 'Pink' },
  { key: 'green', label: 'Green' },
  { key: 'red', label: 'Red' },
  { key: 'purple', label: 'Purple' },
  { key: 'blue', label: 'Blue' },
  { key: 'gold', label: 'Gold' },
];

interface PromoBannerTabProps {
  token: string;
}

export const PromoBannerTab = ({ token }: PromoBannerTabProps) => {
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState('');
  const [color, setColor] = useState('pink');
  const [expiresAt, setExpiresAt] = useState(''); // YYYY-MM-DD or ''
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchBanner = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/promo-banner`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setEnabled(!!d.enabled);
        setText(d.text || '');
        setColor(d.color || 'pink');
        setExpiresAt(d.expires_at ? String(d.expires_at).slice(0, 10) : '');
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBanner(); }, [fetchBanner]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/promo-banner`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ enabled, text: text.trim(), color, expires_at: expiresAt || null }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      } else {
        alert('Failed to save banner');
      }
    } catch {
      alert('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const grad = PROMO_COLOR_MAP[color] || PROMO_COLOR_MAP.pink;
  const today = new Date().toISOString().slice(0, 10);
  const isExpired = !!expiresAt && expiresAt < today;

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="promo-banner-management">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Megaphone className="w-5 h-5 text-pink-600" /> Promo Banner
          </CardTitle>
          <CardDescription>
            Show a promotional banner at the top of the homepage. Change the text and color, and optionally set a date after which it hides automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Live preview */}
          <div>
            <Label className="text-xs text-muted-foreground">Preview</Label>
            <div className={`mt-1 rounded-lg bg-gradient-to-r ${grad} text-white py-3 px-4`}>
              <div className="flex items-center justify-center gap-2 text-center">
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="text-sm sm:text-base font-extrabold tracking-wide uppercase">
                  {text.trim() || 'Your banner text here'}
                </span>
                <Sparkles className="w-4 h-4 shrink-0" />
              </div>
            </div>
          </div>

          {/* Enabled toggle */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="banner-enabled" className="font-medium">Show banner on website</Label>
              <p className="text-xs text-muted-foreground">Turn off to hide the banner immediately.</p>
            </div>
            <Switch id="banner-enabled" checked={enabled} onCheckedChange={setEnabled} data-testid="banner-enabled-switch" />
          </div>

          {/* Text */}
          <div className="space-y-1.5">
            <Label htmlFor="banner-text">Banner text</Label>
            <Input
              id="banner-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Dada and Ji Yeon back in town NOW"
              maxLength={120}
              data-testid="banner-text-input"
            />
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setColor(c.key)}
                  data-testid={`banner-color-${c.key}`}
                  className={`h-9 px-3 rounded-full text-white text-sm font-medium bg-gradient-to-r ${PROMO_COLOR_MAP[c.key]} transition-all ${color === c.key ? 'ring-2 ring-offset-2 ring-gray-800 scale-105' : 'opacity-80 hover:opacity-100'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Expiry date */}
          <div className="space-y-1.5">
            <Label htmlFor="banner-expiry">Hide automatically after (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="banner-expiry"
                type="date"
                value={expiresAt}
                min={today}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="max-w-[200px]"
                data-testid="banner-expiry-input"
              />
              {expiresAt && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setExpiresAt('')} data-testid="banner-expiry-clear">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {expiresAt
                ? (isExpired
                    ? 'This date is in the past — the banner will not show on the website.'
                    : `The banner will show through ${expiresAt} and hide the next day.`)
                : 'No end date — the banner stays until you turn it off.'}
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="banner-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Banner</>}
            </Button>
            {saved && <span className="text-sm text-green-600 font-medium" data-testid="banner-saved-msg">Saved!</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
